<script setup lang="ts">
import { markRaw, ref, watch } from 'vue'
import type { Component } from 'vue'

import type { AppIconEmits, AppIconProps } from './types'

const iconModules = import.meta.glob<{ default: Component }>('./components/Icon*.vue')

const props = withDefaults(defineProps<AppIconProps>(), {
  size: '1em',
})

const emits = defineEmits<AppIconEmits>()

const component = ref<Component | string | null>(null)
const attrs = ref<Record<string, unknown>>({})

let loadToken = 0

const loadIcon = async() => {
  const token = ++loadToken
  const name = props.name

  if (name.includes('.')) {
    component.value = 'img'
    attrs.value = {
      src: name,
      onError: (e: Event) => {
        emits('load:error', e)
      },
    }
    return
  }

  attrs.value = {}
  const loader = iconModules[`./components/Icon${name}.vue`]

  if (!loader) {
    component.value = null
    console.warn(`[AppIcon] Icon "${name}" not found`)
    return
  }

  const loadedModule = await loader()
  // Bail if a newer load started while this import was resolving.
  if (token !== loadToken) return
  component.value = markRaw(loadedModule.default)
}

watch(() => props.name, loadIcon, { immediate: true })
</script>

<template>
  <component
    :is="component"
    v-if="component"
    v-bind="attrs"
    class="app-icon"
    data-test-id="app-icon"
    :style="{
      width: props.size,
      height: props.size,
    }"
  />
</template>

<style lang="scss" scoped>
.app-icon {
  display: inline-block;
  flex-shrink: 0;
  line-height: 0;
}
</style>
